import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import { commissionService } from "@/features/commissions/services/commission.service";
import { useCommissionSummary } from "@/features/commissions/hooks/useCommissionSummary";
import { CommissionPeriodFilter } from "@/features/commissions/components/CommissionPeriodFilter";
import { CommissionSummaryTable } from "@/features/commissions/components/CommissionSummaryTable";
import { CommissionFormDialog } from "@/features/commissions/components/CommissionFormDialog";
import { SellerCommissionsModal } from "@/features/commissions/components/SellerCommissionsModal";
import type {
  CommissionSummaryDTO,
  CommissionUpsertRequestDTO,
} from "@/features/commissions/types/commission.types";

function getCurrentMonthRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const startDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const endDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return { startDate, endDate };
}

export function ComissoesPage() {
  const initialPeriod = useMemo(() => getCurrentMonthRange(), []);
  const summary = useCommissionSummary({ initialPeriod });

  const [selectedSeller, setSelectedSeller] =
    useState<CommissionSummaryDTO | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const handleOpenDetails = useCallback((target: CommissionSummaryDTO) => {
    setSelectedSeller(target);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedSeller(null);
  }, []);

  const handleApplyPeriod = useCallback(
    (period: { startDate: string; endDate: string }) => {
      summary.setPeriod(period);
    },
    [summary],
  );

  const handleOpenCreate = useCallback(() => {
    setCreateOpen(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const handleCreateSubmit = async (data: CommissionUpsertRequestDTO) => {
    setCreateLoading(true);
    try {
      await commissionService.create(data);
      toast.success("Comissão criada");
      setCreateOpen(false);
      summary.reload();
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Comissões</h1>
          <p className="text-muted-foreground">
            Total de comissões calculadas por vendedor no período.
          </p>
        </div>
        <div className="flex gap-2 self-start md:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={summary.reload}
            disabled={summary.loading}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
          <Can permission={ACTIONS.commissions.create}>
            <Button size="sm" onClick={handleOpenCreate} className="gap-2">
              <Plus className="w-4 h-4" />
              Nova Comissão
            </Button>
          </Can>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-6">
          <CommissionPeriodFilter
            startDate={summary.period.startDate}
            endDate={summary.period.endDate}
            loading={summary.loading}
            onApply={handleApplyPeriod}
            idPrefix="commission-summary"
          />

          {summary.loading && summary.data.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <Spinner className="w-6 h-6" />
            </div>
          ) : summary.error && summary.data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-destructive">{summary.error}</p>
              <Button variant="outline" size="sm" onClick={summary.reload}>
                Tentar novamente
              </Button>
            </div>
          ) : summary.data.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              Nenhuma comissão encontrada no período selecionado.
            </div>
          ) : (
            <CommissionSummaryTable
              summaries={summary.data}
              loading={summary.loading}
              onOpenDetails={handleOpenDetails}
            />
          )}
        </CardContent>
      </Card>

      <SellerCommissionsModal
        open={selectedSeller !== null}
        sellerId={selectedSeller?.sellerId ?? null}
        sellerName={selectedSeller?.sellerName ?? ""}
        initialStartDate={summary.period.startDate}
        initialEndDate={summary.period.endDate}
        onClose={handleCloseModal}
        onMutate={summary.reload}
      />

      <CommissionFormDialog
        open={createOpen}
        loading={createLoading}
        lockedSellerId={null}
        commission={null}
        onClose={handleCloseCreate}
        onSubmit={handleCreateSubmit}
      />
    </div>
  );
}
